// Settings v1 (rebuild.md §4.9, task 3.1): captions, audio, motion, contrast, text size, font, video,
// chapter intros, timing, downloads and storage, save slots (switch, label, export/import), Classroom mode.
import { useEffect, useRef, useState } from 'preact/hooks';
import { installAvailable, isIos, isStandalone, promptInstall } from '@app/install.ts';
import { flushSave, save, settings, switchSlot, updateSave, updateSettings } from '@app/state.ts';
import { CHAPTERS } from '@content/chapters.ts';
import { downloadAll, formatBytes, freeSpace, packs, refreshPackStates, removePack } from '@media/packs.ts';
import { exportSave, importSave, saveSlot, slotSummaries, storageUnavailable, type Save } from '@save/store.ts';
import { Button, Card, Slider, Toggle } from '@ui/core.tsx';
import { showToast } from '@ui/toast.tsx';
import { Screen, useNavigate } from './Shell.tsx';

function Select<T extends string>(p: { label: string; value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <label class="select-row">
      <span>{p.label}</span>
      <select class="select" value={p.value} onChange={(e) => p.onChange((e.target as HTMLSelectElement).value as T)}>
        {p.options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}

const pct = (v: number) => `${Math.round(v * 100)}%`;

export function Settings() {
  const st = settings.value;
  const navigate = useNavigate();
  const [slots, setSlots] = useState<{ slot: 1 | 2 | 3; save: Save | null }[]>([]);
  const [free, setFree] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const refresh = () => void slotSummaries().then(setSlots);
  useEffect(() => {
    void flushSave().then(refresh);
    void refreshPackStates();
    void freeSpace().then(setFree);
  }, [save.value.slot]);

  const doExport = async () => {
    const text = await exportSave(save.value);
    const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `forge-and-co-slot${save.value.slot}${save.value.label ? `-${save.value.label.replace(/\W+/g, '-')}` : ''}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const doImport = async (file: File) => {
    try {
      const s = await importSave(await file.text(), save.value.slot);
      await saveSlot(s);
      await switchSlot(s.slot);
      showToast('correct', `Progress imported into slot ${s.slot}.`);
      refresh();
    } catch (e) {
      showToast('wrong', (e as Error).message);
    }
  };
  const downloaded = Object.entries(packs.value).filter(([, p]) => p.status === 'ready');

  return (
    <Screen title="Settings">
      {storageUnavailable.value && <p class="banner">Progress won’t be saved on this device (storage is unavailable).</p>}
      <div class="settings">
        <Card title="Captions and film">
          <Toggle label="Captions" checked={st.captions} onChange={(v) => updateSettings({ captions: v })} />
          <Select label="Caption size" value={st.captionSize} onChange={(v) => updateSettings({ captionSize: v })} options={[['small', 'Small'], ['medium', 'Medium'], ['large', 'Large']]} />
          <Toggle label="Caption background" checked={st.captionBackground} onChange={(v) => updateSettings({ captionBackground: v })} />
          <Select label="Video quality" value={st.videoQuality} onChange={(v) => updateSettings({ videoQuality: v })} options={[['auto', 'Auto'], ['720', '720p'], ['1080', '1080p'], ['480', 'Data saver']]} />
          <Select
            label="Chapter intros"
            value={st.chapterIntros}
            onChange={(v) => updateSettings({ chapterIntros: v })}
            options={[['always', 'Always'], ['first', 'First time only'], ['guide', 'Guide only'], ['never', 'Never']]}
          />
        </Card>
        <Card title="Sound">
          <Toggle label="Voices" description="Character barks (subtitles always show)" checked={st.voices} onChange={(v) => updateSettings({ voices: v })} />
          <Slider label="Voice" value={st.voiceVolume} min={0} max={1} step={0.05} format={pct} onChange={(v) => updateSettings({ voiceVolume: v })} />
          <Toggle label="Focus music" description="A quiet loop for each chapter while you work" checked={st.focusMusic} onChange={(v) => updateSettings({ focusMusic: v })} />
          <Slider label="Music" value={st.musicVolume} min={0} max={1} step={0.05} format={pct} onChange={(v) => updateSettings({ musicVolume: v })} />
          <Slider label="Ambience" value={st.ambienceVolume} min={0} max={1} step={0.05} format={pct} onChange={(v) => updateSettings({ ambienceVolume: v })} />
          <Slider label="Effects" value={st.sfxVolume} min={0} max={1} step={0.05} format={pct} onChange={(v) => updateSettings({ sfxVolume: v })} />
        </Card>
        <Card title="Display and access">
          <Select label="Reduce motion" value={st.reduceMotion} onChange={(v) => updateSettings({ reduceMotion: v })} options={[['system', 'Follow device'], ['on', 'On'], ['off', 'Off']]} />
          <Toggle label="High contrast" checked={st.highContrast} onChange={(v) => updateSettings({ highContrast: v })} />
          <Slider label="Text size" value={st.textScale} min={1} max={1.5} step={0.05} format={pct} onChange={(v) => updateSettings({ textScale: v })} />
          <Toggle label="Dyslexia-friendly font" description="Lexend" checked={st.dyslexiaFont} onChange={(v) => updateSettings({ dyslexiaFont: v })} />
          <Toggle label="Relaxed timing" description="Doubles timed windows and turns off soft and hard timers" checked={st.relaxedTiming} onChange={(v) => updateSettings({ relaxedTiming: v })} />
          <Toggle label="Tutorial coach marks" checked={st.coachMarks} onChange={(v) => updateSettings({ coachMarks: v })} />
        </Card>
        <Card title="Downloads and storage">
          <p class="dim">{free !== null ? `About ${formatBytes(free)} free on this device.` : 'Storage size unknown.'}</p>
          <p class="dim">{downloaded.length ? `${downloaded.length} chapter(s) downloaded for offline viewing.` : 'No chapters downloaded.'}</p>
          <div class="row">
            <Button onClick={() => void downloadAll(CHAPTERS.map((c) => c.id))}>Download all chapters</Button>
            {downloaded.length > 0 && (
              <Button tone="danger" onClick={() => void Promise.all(downloaded.map(([id]) => removePack(id)))}>
                Remove downloads
              </Button>
            )}
          </div>
        </Card>
        <Card title="Save slots">
          {slots.map(({ slot, save: s }) => (
            <div key={slot} class="slot-row">
              <span class="slot-name">
                Slot {slot}
                {s?.label ? ` · ${s.label}` : ''} {s ? <span class="dim">({Object.keys(s.results).length} filed)</span> : <span class="dim">(empty)</span>}
              </span>
              {save.value.slot === slot ? (
                <span class="tag">Active</span>
              ) : (
                <Button onClick={() => void switchSlot(slot)}>Use</Button>
              )}
            </div>
          ))}
          <label class="select-row">
            <span>Label for this slot (optional)</span>
            <input class="text-field" maxLength={40} value={save.value.label} onInput={(e) => updateSave((s) => ({ ...s, label: (e.target as HTMLInputElement).value }))} />
          </label>
          <div class="row">
            <Button onClick={() => void doExport()}>Export progress</Button>
            <Button onClick={() => fileRef.current?.click()}>Import progress</Button>
            <input ref={fileRef} type="file" accept="application/json,.json" class="visually-hidden" aria-label="Import a progress file" onChange={(e) => {
              const f = (e.target as HTMLInputElement).files?.[0];
              if (f) void doImport(f);
            }} />
          </div>
        </Card>
        <Card title="Classroom">
          <Toggle
            label="Classroom mode"
            description="Unlocks every stage and instrument, shows syllabus codes, print-friendly Notebook. Deep links: #/stage/ch01?tier=rationed"
            checked={save.value.classroom}
            onChange={(v) => updateSave((s) => ({ ...s, classroom: v }))}
          />
        </Card>
        <Card title="About">
          <p>Forge &amp; Co.: Last Reagent · version {__APP_VERSION__}</p>
          <p class="dim">No accounts, no analytics. Nothing leaves this device.</p>
          {!isStandalone() &&
            (installAvailable.value ? (
              <Button onClick={() => void promptInstall()}>Install the game on this device</Button>
            ) : isIos() ? (
              <p class="dim">To install on iPhone or iPad: tap Share, then “Add to Home Screen”. Installed, it opens full-screen and works offline.</p>
            ) : (
              <p class="dim">To install: use your browser’s “Install app” or “Add to Home Screen” menu item. Installed, it opens full-screen and works offline.</p>
            ))}
          <Button onClick={() => navigate('/credits')}>Credits and licences</Button>
        </Card>
      </div>
    </Screen>
  );
}
